import React, { useEffect, useState } from "react";
import Select from "react-select";
import axios from "axios";
import TopNav from "../../components/navigation/TopNav";
import { useTranslation } from "react-i18next";

import StandardScreen, { ScreenContent } from "../../components/layout/StandardScreen";
import Stack from "../../components/layout/Stack";
import Card from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import FormField from "../../components/forms/FormField";
import TextInput from "../../components/forms/TextInput";
import StatusMessage from "../../components/ui/StatusMessage";

export default function GodResetPassword() {
  const { t } = useTranslation();

  const [allUsers, setAllUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [message, setMessage] = useState("");
  const [lastAction, setLastAction] = useState("");

  useEffect(() => {
    axios.defaults.headers.common["Authorization"] = `Bearer ${localStorage.getItem("token")}`;
    fetchAllUsers();
  }, []);

  const fetchAllUsers = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/god/users`);
      const uniqueUsersMap = {};
      res.data.forEach((user) => {
        if (!uniqueUsersMap[user.user_id]) {
          uniqueUsersMap[user.user_id] = user;
        }
      });
      setAllUsers(Object.values(uniqueUsersMap));
    } catch (err) {
      console.error("❌ Failed to fetch users");
    }
  };

  const handleChangePassword = async () => {
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/god/reset-password`, {
        user_id: selectedUser,
        new_password: newPassword,
      });

      setMessage(t("admin_reset_password.messages.success"));
      setNewPassword("");
    } catch (err) {
      setMessage(t("admin_reset_password.messages.failure"));
    }
  };

  const selectedUserOption = selectedUser
    ? {
        value: selectedUser,
        label:
          allUsers.find((u) => u.user_id === selectedUser)?.fullname +
          " | " +
          allUsers.find((u) => u.user_id === selectedUser)?.userno +
          " | " +
          allUsers.find((u) => u.user_id === selectedUser)?.sponsor,
      }
    : null;

  return (
    <StandardScreen
      background="default"
      topNav={<TopNav title={t("admin_reset_password.title")} mode="main" />}
    >
      <ScreenContent>
        <div style={{ maxWidth: 720, width: "100%", margin: "0 auto" }}>
          <Stack gap="4">
            <Card>
              <Stack gap="4">
                <h1
                  style={{
                    margin: 0,
                    fontSize: "1.5rem",
                    fontWeight: 700,
                  }}
                >
                  {t("admin_reset_password.title")}
                </h1>

                <FormField label={t("admin_reset_password.fields.select_user.label")}>
                  <div>
                    <Select
                      classNamePrefix="react-select"
                      options={allUsers.map((user) => ({
                        value: user.user_id,
                        label: `${user.fullname} | ${user.userno} | ${user.sponsor}`,
                      }))}
                      value={selectedUserOption}
                      onChange={(option) => {
                        setSelectedUser(option?.value || "");
                        setNewPassword("");
                        setMessage("");
                        setLastAction("");
                      }}
                      placeholder={t("admin_reset_password.fields.select_user.placeholder")}
                    />
                  </div>
                </FormField>

                {selectedUser && (
                  <FormField label={t("admin_reset_password.fields.password.label")}>
                    <TextInput
                      type="password"
                      value={newPassword}
                      onChange={(e) => {
                        setNewPassword(e.target.value);
                        setLastAction("");
                        setMessage("");
                      }}
                      placeholder={t("admin_reset_password.fields.password.placeholder")}
                      maxLength={4}
                      inputMode="numeric"
                    />
                  </FormField>
                )}

                {selectedUser && (
                  <Button
                    onClick={() => {
                      setLastAction("change");
                      handleChangePassword();
                    }}
                    disabled={
                      !newPassword ||
                      newPassword.length !== 4 ||
                      (lastAction === "change" &&
                        (message.startsWith("✅") || message.startsWith("❌")))
                    }
                    fullWidth
                  >
                    {lastAction === "change"
                      ? message.startsWith("✅")
                        ? t("admin_reset_password.status.success_short")
                        : message.startsWith("❌")
                        ? `${t("admin_reset_password.status.error_short")}: ${message.replace("❌ ", "")}`
                        : t("admin_reset_password.actions.submit")
                      : t("admin_reset_password.actions.submit")}
                  </Button>
                )}
              </Stack>
            </Card>

            <StatusMessage message={message} />
          </Stack>
        </div>
      </ScreenContent>
    </StandardScreen>
  );
}